import {
  DropDownSelectBoxOption,
  InfoAlert,
  TokenSelectionDropDownSelectBox,
} from '@/components';
import { useWalletsContext } from '@/components/providers';
import APP_CONFIG from '@/config/app';
import {
  TokenInterface,
  WalletTokenInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { replaceTokensInString } from '@/utils/functionalUtils';
import { YIELD_BEARING_ASSET_ACTIONS_CARD } from '@/utils/strings';
import React, { useCallback, useMemo } from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
interface Props {
  yieldBearingAsset: YieldBearingAssetInterface;
  tokenToInvest?: WalletTokenInterface | TokenInterface;
  handleSelectChange: (
    selectedToken: WalletTokenInterface | TokenInterface,
  ) => void;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
  errors?: FieldErrors;
}
const YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox =
  React.forwardRef<any, Props>(
    (
      {
        yieldBearingAsset,
        tokenToInvest,
        handleSelectChange,
        register,
        setValue,
        clearErrors,
        errors,
      },
      ref,
    ) => {
      const { getEligibleTokensForDeposit } = useWalletsContext();
      const tokensToBeListedAsOptions = useMemo(() => {
        if (!yieldBearingAsset) {
          return [];
        }

        const eligibleTokens = getEligibleTokensForDeposit();
        if (
          Array.isArray(yieldBearingAsset.tokensSupportedForDeposit) &&
          yieldBearingAsset.tokensSupportedForDeposit.length > 0
        ) {
          const eligibleTokensById = new Map(
            eligibleTokens.map((token) => [token.id, token]),
          );

          return yieldBearingAsset.tokensSupportedForDeposit
            .map((token) => {
              const tokenId = token.id;
              return tokenId ? eligibleTokensById.get(tokenId) : undefined;
            })
            .filter((token): token is WalletTokenInterface => Boolean(token));
        }

        const supportedBlockchains = new Set(
          yieldBearingAsset.underlyingYieldBearingAssets.map(
            (asset) => asset.blockchain,
          ),
        );
        return eligibleTokens.filter((token) => {
          const isExcluded =
            APP_CONFIG.BLOCKCHAINS_NOT_SUPPORTED_FOR_YIELD_BEARING_ASSET_DEPLOYMENT.includes(
              token.blockchain,
            );
          if (isExcluded) {
            return false;
          }
          if (!APP_CONFIG.ALLOW_CROSS_CHAIN_YIELD_DEPLOYMENTS) {
            return supportedBlockchains.has(token.blockchain);
          }
          return true;
        });
      }, [getEligibleTokensForDeposit, yieldBearingAsset]);
      const replacements = useMemo(
        () => ({
          yield_bearing_asset_ticker: yieldBearingAsset?.ticker || '',
          yield_bearing_asset_name: yieldBearingAsset?.name || '',
          selected_asset_name: tokenToInvest?.name || '',
          selected_asset_ticker: tokenToInvest?.symbol || '',
        }),
        [yieldBearingAsset, tokenToInvest],
      );
      const label = useMemo(() => {
        return replaceTokensInString(
          YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
            .ASSET_SELECTION_FIELD.LABEL,
          replacements,
        );
      }, [replacements]);
      const tooltip = useMemo(() => {
        return (
          <InfoAlert
            title={replaceTokensInString(
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                .ASSET_SELECTION_FIELD.HELP_TITLE,
              replacements,
            )}
            description={replaceTokensInString(
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                .ASSET_SELECTION_FIELD.HELP_DESCRIPTION,
              replacements,
            )}
            intent="transparent"
          />
        );
      }, [replacements]);
      const handleTokenSelect = useCallback(
        (
          param:
            | React.ChangeEvent<HTMLInputElement>
            | DropDownSelectBoxOption<string>,
        ) => {
          if ('value' in param) {
            const selectedToken = tokensToBeListedAsOptions.find(
              (token) => token.id === param.value,
            );
            if (selectedToken) {
              handleSelectChange(selectedToken);
            }
          }
        },
        [handleSelectChange, tokensToBeListedAsOptions],
      );
      return (
        <TokenSelectionDropDownSelectBox
          name="token-to-invest"
          ref={ref}
          tokensToBeListedAsOptions={tokensToBeListedAsOptions}
          tokenToInvest={tokenToInvest}
          onTokenSelect={handleTokenSelect}
          register={register}
          setValue={setValue}
          clearErrors={clearErrors}
          errors={errors}
          label={label}
          tooltip={tooltip}
          tooltipIsFullBleed
        />
      );
    },
  );
YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox.displayName =
  'YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox';
export default YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox;
